import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Github, ExternalLink } from 'lucide-react';

interface Project {
  id: number;
  title: string;
  description: string;
  image: string;
  technologies: string[];
  githubUrl: string;
  liveUrl: string;
}

const Projects: React.FC = () => {
  const [projects, setProjects] = useState<Project[]>([]);

  useEffect(() => {
    // Fetch projects from data source
    const fetchProjects = async () => {
      try {
        console.log('Fetching projects from /data/projects.json...');
        // Try to fetch from the projects data file first
        const response = await fetch('/data/projects.json');
        console.log('Response status:', response.status);
        if (response.ok) {
          const projectsData = await response.json();
          console.log('Projects data loaded:', projectsData);
          setProjects(projectsData);
        } else {
          console.log('Failed to fetch from JSON, checking localStorage...');
          // If fetch fails, check localStorage as backup
          const savedProjects = localStorage.getItem('projects');
          if (savedProjects) {
            setProjects(JSON.parse(savedProjects));
          } else {
            // If no data anywhere, start with empty array
            console.log('No projects data found, starting with empty array');
            setProjects([]);
          }
        }
      } catch (error) {
        console.error('Failed to fetch projects:', error);
        // Fallback to localStorage if fetch fails
        const savedProjects = localStorage.getItem('projects');
        if (savedProjects) {
          setProjects(JSON.parse(savedProjects));
        } else {
          setProjects([]);
        }
      }
    };

    fetchProjects();
  }, []);

  return (
    <div className="min-h-screen bg-[#000000]">
      {/* Hero Section */}
      <section className="pt-32 pb-16">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className="text-4xl md:text-6xl font-bold tracking-tighter mb-6">
              Our <span className="text-zinc-500">Projects</span>
            </h1>
            <p className="text-zinc-400 text-lg md:text-xl font-medium leading-relaxed">
              Explore our portfolio of engineering solutions that demonstrate our commitment to 
              clean architecture, scalability, and cutting-edge technology.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="pb-32">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {projects.map((project, index) => (
              <motion.div
                key={project.id}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="group"
              >
                <div className="relative overflow-hidden rounded-2xl bg-[#0A0A0A] border border-white/5 hover:border-violet-500/20 transition-all duration-300">
                  {/* Project Image */}
                  <div className="relative h-48 overflow-hidden">
                    <img 
                      src={project.image} 
                      alt={project.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                  </div>

                  {/* Project Content */}
                  <div className="p-6">
                    <h3 className="text-xl font-bold mb-3 tracking-tight group-hover:text-violet-400 transition-colors">
                      {project.title}
                    </h3>
                    <p className="text-zinc-400 text-sm font-medium leading-relaxed mb-4">
                      {project.description}
                    </p>

                    {/* Technologies */}
                    <div className="flex flex-wrap gap-2 mb-6">
                      {project.technologies.map((tech) => (
                        <span 
                          key={tech}
                          className="px-3 py-1 rounded-full bg-white/5 border border-white/10 text-xs font-medium text-zinc-400"
                        >
                          {tech}
                        </span>
                      ))}
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-3">
                      <motion.a
                        href={project.liveUrl}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="flex-1 px-4 py-2.5 rounded-lg bg-violet-600 text-white text-sm font-medium hover:bg-violet-700 transition-colors flex items-center justify-center gap-2"
                      >
                        <ExternalLink size={16} />
                        View Live
                      </motion.a>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Projects;
